<template>
  <div class="CreateEditDashboard">
    <div v-if="canSearchAsset">
      <b-row v-if="!dashboardId" class="mb-4 align-items-end">
        <b-col cols="3" class="text-left">
          <h1 data-cy="NewDashboard-title">New dashboard</h1>
          <label for="name">Choose a name for your dashboard:</label>
          <b-form-input
            data-cy="Dashboard-name"
            placeholder="Dashboard's name"
            v-model="dashboardName"
            id="name"
          ></b-form-input>
        </b-col>
        <b-col class="text-right">
          <b-button
            v-if="!isDashboardValid"
            title="To create a dashboard you have to write a name and select at least one asset"
            variant="outline-primary"
            @click="createNewDashboard"
            :disabled="true"
            >Create dashboard</b-button
          >
          <b-button
            data-cy="Dashboard-create"
            v-else
            variant="outline-primary"
            @click="createNewDashboard"
            >Create dashboard</b-button
          >
        </b-col>
      </b-row>
      <b-row v-else class="mb-4 align-items-end">
        <b-col cols="3" class="text-left">
          <h1 data-cy="NewDashboard-title">Edit dashboard</h1>
          <label for="name">Dashboard name:</label>
          <b-form-input
            data-cy="Dashboard-name"
            placeholder="Dashboard's name"
            v-model="dashboardName"
            id="name"
          ></b-form-input>
        </b-col>
        <b-col class="text-right">
          <b-button
            :title="!isDashboardValid ? 'You have to write a name and select at least one asset' : null"
            :disabled="!isDashboardValid"
            variant="outline-primary"
            @click="editDashboard"
            data-cy="Dashboard-create"
            >Save dashboard</b-button
          >
        </b-col>
      </b-row>
      <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
        <Table
          class="p-0 text-left"
          :items="assets"
          :fields="fields"
          :filterable="true"
          :current-page="currentPage"
          :per-page="perPage"
          :total-rows="totalRows"
          @filtered="onFilterChanged"
          @sort-changed="onSortChanged"
          @page-changed="onPageChanged"
          @per-page-changed="onPerPageChanged"
        >
          <template v-slot:cell(checkbox)="data">
            <div class="text-center" title="Select asset">
              <b-form-checkbox
                @change="onCheckSelectedAssets($event, data.item._id)"
                :checked="isChecked(data.item._id)"
                :data-cy="`Checkbox-asset-${data.item.reference}`"
              ></b-form-checkbox>
            </div>
          </template>
          <template #empty>
            <h4 class="text-center mt-3">There is currently no results.</h4>
            <p class="text-center">
              Try changing the filters to modify your search.
            </p>
          </template>
        </Table>
      </b-overlay>
    </div>
    <PageNotAllowed v-else />
  </div>
</template>

<script>
import PageNotAllowed from '@/components/PageNotAllowed';
import { mapGetters, mapActions } from 'vuex';
import { canSearchAsset } from '@/helpers/rights.js';

export default {
  name: 'CreateEditDashboard',
  props: {
    dashboardId: {
      type: String,
      required: false
    }
  },
  components: {
    PageNotAllowed
  },
  data() {
    return {
      isLoading: false,
      dashboardName: '',
      selectedAssets: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filter: null,
      assets: [],
      fields: [
        {
          key: 'checkbox',
          label: '',
          sortable: false
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true
        },
        {
          key: 'model',
          label: 'Model',
          sortable: true
        },
        {
          key: 'reference',
          label: 'Reference',
          sortable: true
        }
      ]
    };
  },
  computed: {
    ...mapGetters('tenants', ['selectedTenant']),
    ...mapGetters('auth', ['currentUserRights']),
    ...mapGetters('dashboard', ['dashboardList']),
    canSearchAsset,
    isDashboardValid() {
      return this.dashboardName.length > 0 && this.selectedAssets.length > 0;
    }
  },

  async mounted() {
    await this.fetchAssets();
    if (this.dashboardId) {
      await this.fetchDashboardData();
    }
  },

  methods: {
    ...mapActions('dashboard', ['CREATE_DASHBOARD', 'SAVE_DASHBOARD', 'FETCH_DASHBOARDS']),
    async fetchDashboardData() {
      try {
        const response = await this.$kuzzle.document.get(
          this.selectedTenant.index,
          'dashboards',
          this.dashboardId
        );
        this.dashboardName = response._source.name;
        this.selectedAssets = response._source.assetIds;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },

    async fetchAssets() {
      if (!this.canSearchAsset) {
        return;
      }
      this.isLoading = true;
      this.assets = [];

      const assets = [];
      const body = {};

      body.sort = this.sort || { type: { order: 'asc' } };

      if (this.filter) {
        body.query = {
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: ['type.text', 'model.text', 'reference.text']
          }
        };
      }

      const request = {
        index: this.selectedTenant.index,
        controller: 'device-manager/asset',
        action: 'search',
        from: (this.currentPage - 1) * this.perPage,
        size: this.perPage,
        lang: 'elasticsearch',
        body
      };

      try {
        const response = await this.$kuzzle.query(request);

        //I have to go in result to found the result.
        this.totalRows = response.result.total;
        for (const asset of response.result.hits) {
          assets.push({
            _id: asset._id,
            type: asset._source.type,
            model: asset._source.model,
            reference: asset._source.reference
          });
        }
        this.assets = assets;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
      this.isLoading = false;
    },
    async createNewDashboard() {
      this.CREATE_DASHBOARD({
        kuzzle: this.$kuzzle,
        tenantIndex: this.selectedTenant.index,
        selectedAssets: this.selectedAssets,
        dashboardName: this.dashboardName
      })
        .then(response => {
          this.FETCH_DASHBOARDS({
            kuzzle: this.$kuzzle,
            tenantIndex: this.selectedTenant.index
          }).catch(error => {
            this.$log.error(error);
            this.showErrorToast(error.message);
          });
          this.$router.push(`/dashboard/${response}`);
        })
        .catch(error => {
          this.$log.error(error);
          this.showErrorToast(error.message);
        });
    },
    async editDashboard() {
      try {
        const dashboardId = await this.SAVE_DASHBOARD({
          kuzzle: this.$kuzzle,
          tenantIndex: this.selectedTenant.index,
          selectedAssets: this.selectedAssets,
          dashboardName: this.dashboardName,
          dashboardId: this.dashboardId
        })
        await this.FETCH_DASHBOARDS({
          kuzzle: this.$kuzzle,
          tenantIndex: this.selectedTenant.index
        });
        this.$router.push(`/dashboard/${dashboardId}`);
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },
    isChecked(item) {
      return this.selectedAssets.indexOf(item) !== -1;
    },
    onCheckSelectedAssets(checked, item) {
      if (checked) {
        this.selectedAssets.push(item);
      } else {
        if (this.selectedAssets.indexOf(item) !== -1) {
          this.selectedAssets.splice(this.selectedAssets.indexOf(item), 1);
        }
      }
    },
    onFilterChanged(value) {
      this.filter = value;
      this.fetchAssets();
    },
    onSortChanged(value) {
      this.sort = {
        [value.sortBy]: { order: value.sortDesc ? 'desc' : 'asc' }
      };
      this.fetchAssets();
    },
    onPageChanged(value) {
      this.currentPage = value;
      this.fetchAssets();
    },
    onPerPageChanged(value) {
      this.perPage = value;
      this.fetchAssets();
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true
      });
    }
  },

  watch: {
    selectedTenant() {
      this.fetchAssets();
    }
  }
};
</script>
